import { Check, ChevronRight, Copy01 } from '@aktiia/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ModalName } from '../../../../enums';
import { useAccount } from '../../../../features/account';
import { useCopyToClipboard } from '../../../../features/app';
import { useOpenModal } from '../../../../features/modals';
import { useOrganization } from '../../../../features/organization';
import { getUserName } from '../../../../utils';
import { Button, Card } from '../../../common';
import { InlineFormField } from '../../inline-form-field';
import { IOrganizationDetailsProps } from './types';

export const OrganizationDetails = ({ className, expanded }: IOrganizationDetailsProps) => {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const { account } = useAccount();
  const openContactSupportModal = useOpenModal(ModalName.CONTACT_SUPPORT);

  const sharingKey = useMemo(() => `${organization?.linkageKey}`, [organization]);
  const primaryContact = useMemo(() => getUserName(organization?.admin), [organization]);
  const isPrimaryContact = useMemo(() => account?.username === organization?.admin.username, [account, organization]);
  const [isCopied, copyText] = useCopyToClipboard(sharingKey);

  return (
    <Card className={className}>
      <div className="mb-6">
        <h3 className="typography-heading-large">{organization?.name}</h3>
        {!expanded && (
          <p className="typography-paragraph-3 text-text-secondary mt-2">{t('organization.subtitle.organization')}</p>
        )}
      </div>

      {expanded && (
        <>
          <InlineFormField
            className="py-4"
            title={t('organization.field.address')}
            value={organization?.physicalAddress}
          />
          <InlineFormField className="py-4" title={t('organization.field.country')} value={organization?.country} />
        </>
      )}
      <InlineFormField className="py-2" title={t('organization.field.sharingKey')}>
        <Button
          text={isCopied ? t('profile.label.copied') : sharingKey}
          type="tertiary"
          size="sm"
          leftIcon={isCopied ? <Check className="scale-75" /> : <Copy01 className="scale-75" />}
          onClick={copyText}
        />
      </InlineFormField>
      {expanded && (
        <InlineFormField className="py-4" title={t('organization.field.language')} value={organization?.language} />
      )}
      <InlineFormField
        className="py-4"
        title={t('organization.field.primaryContact')}
        value={(!expanded || !isPrimaryContact) && primaryContact}
      >
        {expanded && isPrimaryContact && (
          <Link to="/app/profile" className="typography-inline-link-3 text-text-link cursor-pointer">
            {primaryContact}
          </Link>
        )}
      </InlineFormField>
      <div className="grow" />
      <div className="mt-6">
        {expanded ? (
          <Button text={t('organization.button.support')} size="sm" onClick={() => openContactSupportModal()} />
        ) : (
          <Button
            text={t('organization.button.organization')}
            path="/app/organization/details"
            rightIcon={<ChevronRight />}
            type="tertiary"
          />
        )}
      </div>
    </Card>
  );
};
